import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Undo2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CursorPagination, useCursorPager } from '@nucleus-commerce/admin-kit/data-table';
import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { AdminAuction, AdminAuctionBid } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';
import { Label } from '@nucleus-commerce/ui/components/label';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { membersQueryOptions } from '@/features/team';

import { useAuctionBids } from '../hooks/use-auction-bids';
import { useRetractBid } from '../hooks/use-auction-mutations';
import { canRetract } from '../schemas/auction-rules';

import { ReasonDialog } from './reason-dialog';

const PAGE = 50;

/** Removed bids name who removed them; the member list resolves the id when it may be read. */
function useRemoverName() {
  const { can } = usePermissions();
  const members = useQuery({ ...membersQueryOptions(), enabled: can('staff.read') });
  return (userId: string | null) => {
    if (!userId) return null;
    const member = members.data?.find((m) => m.user.id === userId);
    return member ? (member.user.name ?? member.user.email) : null;
  };
}

/**
 * The full history, newest first: real names and emails, private maximums, the automatic proxy
 * bids and private raises. Removing a shopper's bid takes the automatic bids it set off with it.
 */
export function AuctionBidsCard({
  storeId,
  auction,
  canWrite,
}: {
  storeId: string;
  auction: AdminAuction;
  canWrite: boolean;
}) {
  const { t } = useTranslation('auctions');
  const format = useFormat();
  const removerName = useRemoverName();
  const [showRemoved, setShowRemoved] = useState(true);
  const [cursor, setCursor] = useState<string | undefined>();
  const [retracting, setRetracting] = useState<AdminAuctionBid | null>(null);
  const pager = useCursorPager({
    cursor,
    onNavigate: setCursor,
    resetKey: String(showRemoved),
  });
  const query = useAuctionBids(
    storeId,
    auction.id,
    { includeRetracted: showRemoved, limit: PAGE, ...(cursor ? { cursor } : {}) },
    auction.status === 'live',
  );
  const retract = useRetractBid(storeId, auction.id);
  const bids = query.data?.items ?? [];

  return (
    <Card className="gap-3" data-tour="auctions.detail.bids">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle>{t('bids.title')}</CardTitle>
        <div className="flex items-center gap-2">
          <Switch
            id="auction-show-removed"
            checked={showRemoved}
            onCheckedChange={setShowRemoved}
          />
          <Label htmlFor="auction-show-removed" className="text-muted-foreground text-sm">
            {t('bids.showRemoved')}
          </Label>
        </div>
      </CardHeader>
      <CardContent>
        {query.isPending ? (
          <Skeleton className="h-24" />
        ) : query.error ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : bids.length === 0 && !cursor ? (
          <p className="text-muted-foreground text-sm">{t('bids.empty')}</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table aria-label={t('bids.title')}>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('bids.bidder')}</TableHead>
                    <TableHead className="text-right">{t('bids.amount')}</TableHead>
                    <TableHead className="text-right">{t('bids.max')}</TableHead>
                    <TableHead>{t('bids.kind')}</TableHead>
                    <TableHead>{t('bids.when')}</TableHead>
                    {canWrite ? (
                      <TableHead>
                        <span className="sr-only">{t('bids.actions')}</span>
                      </TableHead>
                    ) : null}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bids.map((bid) => {
                    const removed = bid.retractedAt !== null;
                    const by = removerName(bid.retractedBy);
                    return (
                      <TableRow key={bid.id} className={cn(removed && 'text-muted-foreground')}>
                        <TableCell>
                          <span className="flex flex-col">
                            <span className={cn('font-medium', removed && 'line-through')}>
                              {t('bidder.number', { number: bid.bidderNumber })}{' '}
                              <Link
                                to="/s/$storeId/customers/$customerId"
                                params={{ storeId, customerId: bid.customerId }}
                                className="hover:underline"
                              >
                                {bid.customerName ?? bid.customerEmail}
                              </Link>
                            </span>
                            <span className="text-muted-foreground text-xs">
                              {bid.customerEmail}
                            </span>
                            {removed ? (
                              <span className="text-destructive text-xs">
                                {by
                                  ? t('bids.removedBy', {
                                      who: by,
                                      reason: bid.retractReason ?? '',
                                    })
                                  : t('bids.removed', { reason: bid.retractReason ?? '' })}
                              </span>
                            ) : null}
                          </span>
                        </TableCell>
                        <TableCell
                          className={cn('text-right tabular-nums', removed && 'line-through')}
                        >
                          {format.money(bid.amount)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {bid.maxAmount ? format.money(bid.maxAmount) : t('none')}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" title={t(`kindHint.${bid.kind}`)}>
                            {t(`kind.${bid.kind}`)}
                          </Badge>
                        </TableCell>
                        <TableCell title={format.utc(bid.createdAt)}>
                          {format.dateTime(bid.createdAt, {
                            timeStyle: 'medium',
                            dateStyle: 'short',
                          })}
                        </TableCell>
                        {canWrite ? (
                          <TableCell className="text-right">
                            {removed ? null : (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                disabled={!canRetract(auction, bid)}
                                title={
                                  bid.kind === 'proxy'
                                    ? t('bids.proxyHint')
                                    : auction.status !== 'live'
                                      ? t('bids.notLiveHint')
                                      : undefined
                                }
                                onClick={() => setRetracting(bid)}
                              >
                                <Undo2 aria-hidden />
                                {t('bids.remove')}
                              </Button>
                            )}
                          </TableCell>
                        ) : null}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <CursorPagination
              pager={pager}
              nextCursor={query.data.nextCursor}
              isFetching={query.isFetching}
              count={bids.length}
              limit={PAGE}
            />
          </>
        )}
      </CardContent>
      {canWrite ? (
        <ReasonDialog
          open={retracting !== null}
          onOpenChange={(open) => {
            if (!open) setRetracting(null);
          }}
          title={t('retract.title')}
          description={
            retracting
              ? t('retract.description', {
                  amount: format.money(retracting.amount),
                  bidder: retracting.customerName ?? retracting.customerEmail,
                })
              : ''
          }
          label={t('retract.reason')}
          hint={t('retract.hint')}
          confirmLabel={t('retract.confirm')}
          pending={retract.isPending}
          onConfirm={(reason) =>
            retracting ? retract.mutateAsync({ bidId: retracting.id, reason }) : Promise.resolve()
          }
        />
      ) : null}
    </Card>
  );
}
